<script setup lang="ts">
import * as R from 'ramda'
import { computed } from 'vue'

import { getRandomHtmlId, opChain } from '@collector/shared-utils'

import { vAutofocus } from '../../directives'
import {
  isPressedArrow,
  isPressedBackspace,
  isPressedDelete,
  isPressedEnter,
  isPressedNumber,
  isPressedNumeric,
  isPressedSelectAll,
  isPressedTab,
} from '../../utils'
import FormControl from '../FormControl.vue'
import type {
  TextInputOutlineFocus,
  TextInputSize,
  TextInputType,
  TextInputVariant,
  TextInputWidth,
} from './types'

type FormControlProps = InstanceType<typeof FormControl>['$props']

type Props = {
  type?: TextInputType
  size?: TextInputSize
  autofocus?: boolean
  width?: TextInputWidth
  inputClass?: string
  labelPosition?: FormControlProps['labelPosition']
  maxlength?: number
  min?: number
  max?: number
  numeric?: boolean
  disabled?: boolean
  placeholder?: string
  postfix?: string
  variant?: TextInputVariant
  withError?: boolean
  withOutlineFocus?: TextInputOutlineFocus
  pattern?: RegExp
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  variant: 'default',
  size: 'sm',
})

// emits
type Emits = {
  blur: [{ event: Event; value: string }]
  focus: [{ event: FocusEvent }]
}

defineEmits<Emits>()

// data
const id = getRandomHtmlId()

// model
const inputValue = defineModel<string | null | number>()

// methods
function onKeydown(event: KeyboardEvent): void {
  if (
    props.numeric &&
    !R.anyPass([
      isPressedNumber,
      isPressedNumeric,
      isPressedBackspace,
      isPressedTab,
      isPressedSelectAll,
      isPressedEnter,
      isPressedDelete,
      isPressedArrow,
    ])(event)
  ) {
    event.preventDefault()
  }
}

// computed
const commonClass = computed(() => ({
  'col-start-1 row-start-1': props.postfix,
  'w-full': props.width === 'full',
  'h-10 text-sm': props.size === 'md',
  'px-3': props.size === 'md' && !props.width,
  'h-8 text-xs': props.size === 'sm',
  'px-1': (props.size === 'sm' || props.size === 'xs') && !props.width,
  'h-6 text-xs': props.size === 'xs',
  'text-neutral-dark-0/50': props.disabled,
  [`${props.inputClass}`]: props.inputClass,
}))

const inputClass = computed(() => ({
  'outline-error-5 outline-1 -outline-offset-1 outline-solid': props.withError,
  'focus:outline-primary-4 focus:outline-2 focus:-outline-offset-2 focus:outline-solid':
    props.withOutlineFocus === 'default' && !props.withError,
  'border border-neutral-light-0':
    props.variant === 'default' && !props.withError,
  'border border-neutral-light-6':
    props.variant === 'light' && !props.withError,
  'border border-neutral-light-0/50': props.disabled && !props.withError,
  'bg-neutral-light-10':
    props.variant === 'default' || props.variant === 'light',
  'border border-neutral-dark-0 bg-neutral-light-6': props.variant === 'heavy',
  'pr-10': props.postfix,
}))
</script>

<template>
  <FormControl
    :id
    :size
    :disabled
    :labelPosition
  >
    <template #label>
      <slot />
    </template>
    <div
      class="w-full"
      :class="{
        'grid grid-cols-1 grid-rows-1': props.postfix,
        flex: !props.postfix,
      }"
    >
      <input
        :id
        v-model="inputValue"
        v-autofocus="autofocus"
        :class="[
          commonClass,
          inputClass,
          'enabled:text-neutral-dark-3 placeholder-neutral-dark-3/40 rounded-sm outline-hidden',
        ]"
        :type
        :maxlength
        :placeholder
        :disabled
        :pattern="opChain(props.pattern, (pattern) => pattern.source)"
        @blur="
          $emit('blur', {
            event: $event,
            value: ($event.target as HTMLInputElement).value,
          })
        "
        @keydown="onKeydown"
        @focus="$emit('focus', { event: $event })"
      />
      <div
        v-if="props.postfix"
        class="pointer-events-none flex cursor-text items-center gap-1"
        :class="commonClass"
      >
        <span class="invisible">{{ inputValue }}</span>
        <span class="text-neutral-dark-8">{{ postfix }}</span>
      </div>
      <slot name="content" />
    </div>
  </FormControl>
</template>
